import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EoHttpClient, IHttpParams } from 'ngx-eo-web-components';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { STATUS } from '../../modules/core/models/loading-status.enum';
import {
  IToken,
  LoginResponse,
} from '../../modules/core/models/token-response.model';
import { jwtDecode } from 'jwt-decode';
import { IUserResetPasswordPayload } from '../../modules/core/models/user-reset-password.model';
import { ApiCallHandler } from '../../modules/core/models/apiCallHandler';
import { User } from '../../modules/core/models/user.model';
import { CoreService } from '../../modules/core/core.service';
import { UserData } from '../../modules/core/models/user-data-model';
import { IAuthConfirmBindingModel } from '../../modules/core/models/auth-confirm-bind.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public userResetPasswordCallHandler = new ApiCallHandler<User | null>();
  public userEmailConfirmCallHandler = new ApiCallHandler<User | null>();

  public userDataCallHandler = new ApiCallHandler<any | null>();
  // public decodedToken = new BehaviorSubject<IToken | null>(null);
  public roles$ = new BehaviorSubject<string | null>(null);
  public roleLiterals$ = new BehaviorSubject<string | null>(null);

  constructor(
    private http: EoHttpClient,
    private coreService: CoreService,
    private router: Router
  ) {}
  public RegisterSuccess$ = false; //new BehaviorSubject<boolean | null>(false);
  public tokenError$ = new BehaviorSubject<string | null>(null);
  public tokenStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public token$ = new BehaviorSubject<IToken | null>(null);
  public checkAFMStatus$ = new Subject<string>();
  public loginStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public loginSuccess$ = new BehaviorSubject<any>(null);
  public loginError$ = new BehaviorSubject<any>(null);
  public saveRegister$ = new Subject<string>();

  hasRoles(userRoles: string[]): boolean {
    const token = this.token$.getValue();
    if (!token || !token.role) {
      return false;
    }

    return userRoles.some((role) => {
      // Handle negation (e.g., '!ClinicAdmin')
      if (role.startsWith('!')) {
        const strippedRole = role.substring(1).trim();
        return token.role !== strippedRole;
      }

      return token.role === role;
    });
  }

  //for multiple roles
  // return userRoles.some(role => {
  //   if (role.startsWith('!')) {
  //     const strippedRole = role.substring(1).trim();
  //     return !token.roles?.includes(strippedRole);
  //   }
  //   return token.roles?.includes(role);
  // })

  // public HospitalID: string = this.GetLocalStorageValue('HospitalID') ?? '-1';

  fetchToken(username: string, password: string) {
    // let headers = new HttpHeaders();
    // headers = headers.append(
    //   'Content-Type',
    //   'application/x-www-form-urlencoded'
    // );

    // let body = new URLSearchParams();
    // body.set('grant_type', 'password');
    // body.set('username', username);
    // body.set('password', password);

    const body = {
      username: username,
      password: password,
      edapySpecialtyId: -1,
    };

    const params: IHttpParams = {
      url: `${environment.apiUrl}/v1/Auth/login`,
      body: body,
      status$: this.loginStatus$,
      success$: this.loginSuccess$,
      error$: this.loginError$,
    };

    //this.loginStatus$.next('loading');//STATUS.loading);
    // this.tokenStatus$.next(STATUS.loading);
    this.http.post<LoginResponse>(params).subscribe({
      next: (success) => {
        // const issued = new Date().getTime();
        // const expires = issued + success.expires_in * 1000;
        // success.issued = issued;
        // success.expires = expires;
        //this.loginStatus$.next('success');//STATUS.success);
        this.setToken(success?.token);
      },
      error: (error) => {
        this.loginStatus$.next(STATUS.error); //STATUS.error);
        this.tokenStatus$.next(STATUS.error);
        this.tokenError$.next(error.message);
      },
    });
  }

  resetPassword(payload: IUserResetPasswordPayload) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/reset-password',
      this.userResetPasswordCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  confirmEmail(payload: IAuthConfirmBindingModel) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/confirm',
      this.userEmailConfirmCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  requestResetPassword(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-reset-password?userId=${userId}`,
      this.userResetPasswordCallHandler
    );
    return this.http.get<User>(param);
  }

  requestConfirmEmail(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-confirm-email/${userId}`,
      this.userEmailConfirmCallHandler
    );
    return this.http.get<User>(param);
  }

  setToken(token: string) {
    localStorage.setItem('token', JSON.stringify(token));
    let decodedToken: IToken = jwtDecode(token);

    this.setRoleBasedOnPrivacy(decodedToken);

    //console.log('decodedToken::', decodedToken);

    this.token$.next(decodedToken);
    this.tokenStatus$.next(STATUS.success);
  }

  
  logout() {
    var isCitizen = this.hasRoles(['Citizen']);
    localStorage.clear();
    this.token$.next(null);
    this.tokenError$.next(null);
    if (isCitizen) {
      window.location.assign(`${environment.apiUrl}/v1/GsisOAuth2/logout`);
    } else {
      this.router.navigate(['login'], { queryParams: { ref: 'so' } });
    }
  }

  hydrate() {
    let token = localStorage.getItem('token');
    if (!token) return;

    let decodedToken = jwtDecode<IToken>(token);
    this.setRoleBasedOnPrivacy(decodedToken);
    
    //console.log('decodedToken2::', decodedToken);

    this.token$.next(decodedToken);
  }

  private setRoleBasedOnPrivacy(decodedToken: IToken) {
    if (decodedToken.role === 'HospitalAdmin' || decodedToken.role === 'ClinicAdmin') {
      let isPrivate = decodedToken.isPrivate != null ? decodedToken.isPrivate.toLowerCase() === 'true' ? true : false : false;
      decodedToken.role = isPrivate ? 'Private' + decodedToken.role : decodedToken.role;
    }
  }

  // TokenExpirationCheck() {
  //   let token = localStorage.getItem('token');
  //   if (!token) {
  //     this.logout();
  //     return;
  //   }
  //   let decodedToken = jwtDecode<IToken>(token);
  //   if (new Date(decodedToken.exp * 100) < new Date()) this.logout();
  // }
}
